/**
 * The platform console's "Copy setup from another org" sheet, its logic (POST /v1/admin/orgs/{org_id}/copy-setup,
 * platform-core/internal/twin/org_copy_setup.go): the orgs to copy from, the typed-name confirmation, what a copy takes
 * and what it leaves, what a finished copy wrote in plain lines, and the server's refusals in plain words. Pure.
 *
 * A copy gives an org the setup of another, so the plant's own files can be imported again into it through the org's
 * own extractors. Equipment the target lacks is added and equipment it already has is kept; the readings never travel.
 * Pure, but for runCopySetup, the one call.
 */
import { platformAdminApi, type OrgCopySetupSummary, type OrgSummary } from "@/lib/api/platform-admin";
import { errorCodeOf, errorDetailOf, isNetworkError, statusOf } from "@/lib/api/error";

/** What a copy takes, one line each, as the sheet says it before anything is sent. */
export const COPY_SETUP_COPIED: readonly string[] = [
  "The plant model: equipment the org lacks is added, and equipment it already has is kept.",
  "Plant time and the cost of scrap.",
  "Data sources.",
  "The active import extractors and their streams.",
  "The process windows and scrap targets in force, each noting the org it was copied from.",
  "The defect and parameter word bindings.",
  "Products.",
  "Who set each limit and decided each word binding comes along, as the source org has it.",
];

/** What a copy never takes, one line each. */
export const COPY_SETUP_NOT_COPIED: readonly string[] = [
  "Readings, heats, lots and import runs: import the plant's files again after the copy.",
  "Findings, plant questions, consultant threads and recommendation events.",
  "Members and dashboards.",
];

/** One org to copy from, as the select lists it. */
export interface CopySourceOption {
  id: string;
  label: string;
}

/**
 * The orgs to copy from: every org but the target, by name (case does not order them), a name shared by two orgs
 * followed by the org's slug so the two can be told apart.
 */
export function copySourceOptions(
  orgs: readonly Pick<OrgSummary, "id" | "name" | "slug">[],
  targetId: string,
): CopySourceOption[] {
  const others = orgs.filter((o) => o.id !== targetId);
  const key = (name: string) => name.trim().toLowerCase();
  const sharing = new Map<string, number>();
  for (const o of others) sharing.set(key(o.name), (sharing.get(key(o.name)) ?? 0) + 1);
  return others
    .slice()
    .sort(
      (a, b) =>
        a.name.localeCompare(b.name, "en", { sensitivity: "base" }) || a.slug.localeCompare(b.slug) || a.id.localeCompare(b.id),
    )
    .map((o) => ({ id: o.id, label: (sharing.get(key(o.name)) ?? 0) > 1 ? `${o.name} (${o.slug})` : o.name }));
}

/** Whether what was typed is the target org's name: trimmed, without regard to case, and never for an empty name. */
export function copyNameMatches(typed: string, targetName: string): boolean {
  const name = targetName.trim().toLowerCase();
  return name !== "" && typed.trim().toLowerCase() === name;
}

/** A count that an older server may leave out reads as none, never as NaN. */
const num = (v: number | null | undefined): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);

const thousands = (n: number) => n.toLocaleString("en-US");

/** "1 data source" or "13 data sources". */
function count(n: number, one: string, many: string): string {
  return n === 1 ? `1 ${one}` : `${thousands(n)} ${many}`;
}

/** Equipment or connections: "52 pieces of equipment: 5 added, 47 already there", or all of one kind, or none. */
function addedAgainstKept(added: number, kept: number, one: string, many: string, none: string): string {
  const total = added + kept;
  if (total === 0) return none;
  const noun = count(total, one, many);
  if (kept === 0) return `${noun} added`;
  if (added === 0) return total === 1 ? `${noun}, already there` : `${noun}, all already there`;
  return `${noun}: ${thousands(added)} added, ${thousands(kept)} already there`;
}

/** What a finished copy says: the counts as lines, what was skipped, and what to do next. */
export interface CopySetupReport {
  lines: string[];
  skipped: string[];
  next: string;
}

/** The server's summary of a copy in plain lines, then what it skipped (each line as the server worded it), then the next step. */
export function copySetupReport(s: OrgCopySetupSummary, targetName: string): CopySetupReport {
  const extractors = num(s.extractors);
  const lines = [
    addedAgainstKept(num(s.entities), num(s.entities_matched), "piece of equipment", "pieces of equipment", "No equipment"),
    addedAgainstKept(num(s.connections), num(s.connections_matched), "connection", "connections", "No connections"),
    num(s.data_sources) === 0 ? "No data sources" : count(num(s.data_sources), "data source", "data sources"),
    extractors === 0
      ? "No extractors"
      : `${count(extractors, "extractor", "extractors")} with ${count(num(s.extractor_versions), "active version", "active versions")} ` +
        `and ${count(num(s.streams), "stream", "streams")}`,
    num(s.process_windows) === 0 ? "No process windows" : count(num(s.process_windows), "process window", "process windows"),
    num(s.scrap_targets) === 0 ? "No scrap targets" : count(num(s.scrap_targets), "scrap target", "scrap targets"),
    num(s.vocab_bindings) === 0 ? "No word bindings" : count(num(s.vocab_bindings), "word binding", "word bindings"),
    ...(num(s.defect_types) > 0
      ? [count(num(s.defect_types), "defect type of the org's own", "defect types of the org's own")]
      : []),
    num(s.products) === 0 ? "No products" : count(num(s.products), "product", "products"),
    s.plant_time ? "Plant time and the cost of scrap" : "No plant time or cost of scrap was set in the source",
  ];
  const skipped = (Array.isArray(s.skipped) ? s.skipped : []).filter((x) => x.trim() !== "");
  return { lines, skipped, next: `Import the plant's files into ${targetName} from its Data sources page.` };
}

/** The server's refusal of a copy, in plain words. */
export function copySetupErrorWords(err: unknown, targetName: string): string {
  const status = statusOf(err);
  const code = errorCodeOf(err);
  if (status === 401) return "Your session has ended: sign in again.";
  if (status === 403) return "Only a platform admin can copy a setup into an org.";
  if (code === "target_not_empty") {
    return errorDetailOf(err) ?? `${targetName} already holds data of its own, so a setup cannot be copied into it.`;
  }
  if (code === "target_not_real") return `${targetName} is a demo or rehearsal org: a setup is copied into a real org only.`;
  if (code === "org_not_found") return `${targetName} no longer exists.`;
  if (code === "source_org_not_found") return "The org to copy from no longer exists. Choose another.";
  if (code === "same_org") return "An org cannot take its own setup. Choose another org to copy from.";
  if (status === 400) return errorDetailOf(err) ?? "The server did not take that. Choose the org to copy from and try again.";
  if (isNetworkError(err)) return "The platform could not be reached. Check the connection and try again.";
  return "Something went wrong on the platform. Try again in a moment.";
}

/** Sends the copy and answers what it wrote, or why it did not, in words. Never throws. */
export async function runCopySetup(
  target: Pick<OrgSummary, "id" | "name">,
  sourceOrgId: string,
): Promise<{ summary: OrgCopySetupSummary } | { error: string }> {
  try {
    return { summary: await platformAdminApi.copyOrgSetup(target.id, sourceOrgId) };
  } catch (err) {
    return { error: copySetupErrorWords(err, target.name) };
  }
}
